import auth from '@/authfile';
import useAcvDatadog from '@/composables/useAcvDatadog';
import {
  attachHeldMagicLinkToken,
  holdMagicLinkToken,
  saveMagicLinkToken,
} from '@/utilities/authTokens';
import { authExemptRoutes, uncachedRoutes } from '@/constants/authRoutes';

export default defineNuxtRouteMiddleware(async (to) => {
  if (authExemptRoutes.includes(to.path)) {
    return;
  }

  const config = useRuntimeConfig();
  const auth0 = await auth(
    config.public.AUTH0_DOMAIN,
    config.public.AUTH0_CLIENT_ID,
    config.public.REDIRECT_URL,
    config.public.AUTH0_AUDIENCE,
  );

  const { addRumError } = useAcvDatadog();
  const isAuthenticated = await auth0.isAuthenticated();

  // The emailed link delivers the Magic Link Token as `access_token` in the fragment.
  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  const magicLinkToken = hashParams.get('access_token');

  // Needed by users who aren't fully authenticated yet but whose email we still require.
  const saveEmailFromMagicLinkToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const email = payload['https://acvauctions.com/email'];
      if (email) {
        localStorage.setItem('user_email', email);
      }
    } catch (e) {
      addRumError('Failed to parse user email from magic link token', { error: e });
    }
  };

  // Link opened without an Auth0 session (e.g. a mail app's in-app browser): hold the token
  // through a fresh Auth0 login instead of losing it.
  if (!isAuthenticated && magicLinkToken) {
    holdMagicLinkToken(magicLinkToken);
    saveEmailFromMagicLinkToken(magicLinkToken);
    return navigateTo('/continue-sign-in');
  }

  // Returning from Auth0: the session exists now, so attach any held token.
  if (!isAuthenticated && to.query?.code && to.query?.state) {
    const result = await auth0.handleRedirectCallback();
    attachHeldMagicLinkToken();
    return result;
  }

  if (!isAuthenticated) {
    localStorage.removeItem('magic_link_token');
    if (!uncachedRoutes.includes(to.fullPath)) {
      localStorage.setItem('cachedPath', JSON.stringify(to.fullPath));
    }
    await auth0.loginWithRedirect();
    return abortNavigation('Login required');
  }

  const token = await auth0.getTokenSilently();
  localStorage.setItem('access_token', token);

  // Extract and store user email from Auth0 token
  // This is used during the signup process because user information is not available in our system yet
  const tokenString = config.public.AUTH0_CLIENT_ID;
  let authToken;
  try {
    authToken = JSON.parse(
      localStorage.getItem(
        `@@auth0spajs@@::${tokenString}::transport-load-board::openid profile email`,
      ),
    );
    localStorage.setItem('user_email', authToken.body?.decodedToken?.user?.email);
  } catch (e) {
    addRumError('Failed to parse user email from auth token', {
      error: e,
      authToken,
      tokenString,
    });
  }

  // Link opened in the browser that requested it.
  if (magicLinkToken) {
    saveMagicLinkToken(magicLinkToken);
    saveEmailFromMagicLinkToken(magicLinkToken);
  } else if (hashParams.has('error')) {
    console.error(
      'Error during authentication:',
      hashParams.get('error'),
      hashParams.get('error_description'),
    );
  }
});
